import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Statistic,
  Row,
  Col,
  Tag,
  Modal,
  message,
  Descriptions,
  Alert,
  Space,
  Progress,
  Typography,
  Empty,
  Tabs,
  Slider
} from 'antd';
import {
  SecurityScanOutlined,
  WarningOutlined,
  SafetyOutlined,
  EnvironmentOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  EyeOutlined,
  DownloadOutlined,
  SearchOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Line, Pie } from '@ant-design/charts';
import API from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * 风险管理组件
 * 用于管理员查看和管理异地登录风险评估
 */
const RiskManagement = () => {
  const [loading, setLoading] = useState(false);
  const [systemStats, setSystemStats] = useState(null);
  const [riskLogs, setRiskLogs] = useState([]);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [countryModalVisible, setCountryModalVisible] = useState(false);
  const [userTestModalVisible, setUserTestModalVisible] = useState(false);
  const [selectedRiskLog, setSelectedRiskLog] = useState(null);
  const [logDetailModalVisible, setLogDetailModalVisible] = useState(false);
  const [logStatistics, setLogStatistics] = useState(null);
  const [riskTrend, setRiskTrend] = useState([]);
  const [configHistoryVisible, setConfigHistoryVisible] = useState(false);
  const [configHistory, setConfigHistory] = useState([]);
  const [cacheStatsVisible, setCacheStatsVisible] = useState(false);
  const [cacheStats, setCacheStats] = useState(null);
  const [supportedCountries, setSupportedCountries] = useState({
    countries: [],
    continentGroups: {},
    stats: { totalCountries: 0, continents: 0, continentBreakdown: {} }
  });
  const [locationStats, setLocationStats] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);
  const [searchForm] = Form.useForm();
  const [testForm] = Form.useForm();
  const [configForm] = Form.useForm();
  const [locationForm] = Form.useForm();
  const [countryForm] = Form.useForm();
  const [userTestForm] = Form.useForm();

  // 获取风险配置
  const fetchRiskConfig = async () => {
    try {
      const response = await API.get('/api/admin/risk-management/config');
      const config = response.data.config || response.data;
      
      // 设置基础配置表单值
      configForm.setFieldsValue({
        // 基础风险配置
        threshold: config.RISK_THRESHOLD,
        
        // 权重配置
        LOCATION_DISTANCE: config.WEIGHTS?.LOCATION_DISTANCE,
        DEVICE_FAMILIARITY: config.WEIGHTS?.DEVICE_FAMILIARITY,
        TIME_PATTERN: config.WEIGHTS?.TIME_PATTERN,
        LOGIN_FREQUENCY: config.WEIGHTS?.LOGIN_FREQUENCY,
        IP_REPUTATION: config.WEIGHTS?.IP_REPUTATION,
        
        // 地理位置配置
        FAMILIAR_THRESHOLD_KM: config.LOCATION?.FAMILIAR_THRESHOLD_KM,
        FREQUENT_LOCATION_RATIO: config.LOCATION?.FREQUENT_LOCATION_RATIO,
        NEARBY_LOCATION_RATIO: config.LOCATION?.NEARBY_LOCATION_RATIO,
        
        // 设备配置
        IDENTICAL: config.DEVICE?.SIMILARITY_THRESHOLDS?.IDENTICAL,
        VERY_SIMILAR: config.DEVICE?.SIMILARITY_THRESHOLDS?.VERY_SIMILAR,
        SIMILAR: config.DEVICE?.SIMILARITY_THRESHOLDS?.SIMILAR,
        SOMEWHAT_SIMILAR: config.DEVICE?.SIMILARITY_THRESHOLDS?.SOMEWHAT_SIMILAR,
        
        // 时间模式配置
        WINDOW_HOURS: config.TIME_PATTERN?.WINDOW_HOURS,
        MIN_HISTORY_COUNT: config.TIME_PATTERN?.MIN_HISTORY_COUNT,
        FREQUENT_THRESHOLD: config.TIME_PATTERN?.FREQUENT_THRESHOLD,
        OCCASIONAL_THRESHOLD: config.TIME_PATTERN?.OCCASIONAL_THRESHOLD,
        
        // 频率配置
        MAX_LOGINS_24H: config.FREQUENCY?.MAX_LOGINS_24H,
        MAX_LOGINS_1H: config.FREQUENCY?.MAX_LOGINS_1H,
        SUSPICIOUS_FREQUENCY_SCORE: config.FREQUENCY?.SUSPICIOUS_FREQUENCY_SCORE,
        HIGH_FREQUENCY_SCORE: config.FREQUENCY?.HIGH_FREQUENCY_SCORE,
        
        // IP信誉配置
        LOCAL_IP_SCORE: config.IP_REPUTATION?.LOCAL_IP_SCORE,
        SUSPICIOUS_IP_SCORE: config.IP_REPUTATION?.SUSPICIOUS_IP_SCORE,
        NORMAL_IP_SCORE: config.IP_REPUTATION?.NORMAL_IP_SCORE,
        
        // 分析天数
        ANALYSIS_DAYS: config.ANALYSIS_DAYS,
        
        // 特殊规则配置
        NEW_USER_DAYS: config.SPECIAL_RULES?.NEW_USER_DAYS,
        NEW_USER_THRESHOLD_ADJUSTMENT: config.SPECIAL_RULES?.NEW_USER_THRESHOLD_ADJUSTMENT,
        VIP_THRESHOLD_ADJUSTMENT: config.SPECIAL_RULES?.VIP_THRESHOLD_ADJUSTMENT,
        ADMIN_THRESHOLD_ADJUSTMENT: config.SPECIAL_RULES?.ADMIN_THRESHOLD_ADJUSTMENT,
        FAILED_LOGIN_ADJUSTMENT: config.SPECIAL_RULES?.FAILED_LOGIN_ADJUSTMENT
      });
    } catch (error) {
      console.error('获取风险配置失败:', error);
      message.error('获取风险配置失败');
    }
  };

  // 获取系统风险统计
  const fetchSystemStats = async (days = 7) => {
    try {
      const response = await API.get(`/api/admin/risk-management/system-stats?days=${days}`);
      const stats = response.data.stats || response.data;
      setSystemStats({
        ...stats,
        analysisWindow: response.data.analysisWindow || `${days}天`,
        timestamp: response.data.timestamp
      });
    } catch (error) {
      console.error('获取系统统计失败:', error);
      message.error('获取系统统计失败');
      setSystemStats(null);
    }
  };

  // 获取用户风险统计 (已废弃，使用新的日志统计接口)
  const fetchUserRiskStats = async () => {
    try {
      // 这个接口已经不存在，使用日志统计代替
      await fetchLogStatistics();
    } catch (error) {
      console.error('获取用户统计失败:', error);
      message.error('获取用户统计失败');
    }
  };

  // 获取日志统计信息
  const fetchLogStatistics = async (days = 7) => {
    try {
      const response = await API.get(`/api/admin/risk-management/log-statistics?days=${days}`);
      return response.data.stats || response.data;
    } catch (error) {
      console.error('获取日志统计失败:', error);
      message.error('获取日志统计失败');
      return null;
    }
  };

  // 获取风险趋势
  const fetchRiskTrend = async (days = 7) => {
    try {
      const response = await API.get(`/api/admin/risk-management/risk-trend?days=${days}`);
      return response.data.trend || response.data;
    } catch (error) {
      console.error('获取风险趋势失败:', error);
      message.error('获取风险趋势失败');
      return [];
    }
  };

  // 获取日志详情
  const fetchLogDetail = async (logId) => {
    try {
      const response = await API.get(`/api/admin/risk-management/log-detail/${logId}`);
      return response.data.log || response.data;
    } catch (error) {
      console.error('获取日志详情失败:', error);
      message.error('获取日志详情失败');
      return null;
    }
  };

  // 清理过期日志
  const cleanupLogs = async (days = 90) => {
    try {
      const response = await API.post('/api/admin/risk-management/cleanup-logs', { days });
      return response.data;
    } catch (error) {
      console.error('清理日志失败:', error);
      throw error;
    }
  };

  // 重置配置为默认值
  const resetConfigToDefault = async () => {
    try {
      const response = await API.post('/api/admin/risk-management/config/reset', {
        configType: 'risk_assessment'
      });
      return response.data;
    } catch (error) {
      console.error('重置配置失败:', error);
      throw error;
    }
  };

  // 获取配置历史记录
  const fetchConfigHistory = async (limit = 50) => {
    try {
      const response = await API.get('/api/admin/risk-management/config/history', {
        params: {
          configType: 'risk_assessment',
          limit: limit
        }
      });
      return response.data.history || response.data;
    } catch (error) {
      console.error('获取配置历史失败:', error);
      throw error;
    }
  };

  // 获取配置缓存统计
  const fetchCacheStats = async () => {
    try {
      const response = await API.get('/api/admin/risk-management/config/cache-stats');
      return response.data.cacheStats || response.data;
    } catch (error) {
      console.error('获取缓存统计失败:', error);
      throw error;
    }
  };

  // 清除配置缓存
  const clearConfigCache = async (cacheKey = null) => {
    try {
      const requestData = cacheKey ? { cacheKey } : {};
      const response = await API.post('/api/admin/risk-management/config/clear-cache', requestData);
      return response.data;
    } catch (error) {
      console.error('清除缓存失败:', error);
      throw error;
    }
  };

  // 获取风险日志
  const fetchRiskLogs = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 50,
        ...(params.userId && { userId: params.userId }),
        ...(params.riskLevel && { riskLevel: params.riskLevel }),
        ...(params.action && { action: params.action }),
        ...(params.startDate && { startDate: params.startDate }),
        ...(params.endDate && { endDate: params.endDate }),
        ...(params.ipAddress && { ipAddress: params.ipAddress }),
        ...(params.username && { username: params.username })
      };
      const response = await API.get('/api/admin/risk-management/assessment-logs', { params: queryParams });
      setRiskLogs(response.data.logs || response.data.data || []);
    } catch (error) {
      console.error('获取风险日志失败:', error);
      message.error('获取风险日志失败');
      setRiskLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取支持的国家列表
  const fetchSupportedCountries = async () => {
    try {
      const response = await API.get('/api/admin/risk-management/countries');
      // 根据新接口格式处理数据
      const data = response.data;
      const countries = data.countries || response.data;
      const countryList = Array.isArray(countries) ? countries : [];
      
      // 设置完整的国家数据，包括分组和统计信息
      setSupportedCountries({
        countries: countryList,
        continentGroups: data.continentGroups || {},
        stats: data.stats || {
          totalCountries: countryList.length,
          continents: 0,
          continentBreakdown: {}
        }
      });
    } catch (error) {
      console.error('获取国家列表失败:', error);
      message.error('获取国家列表失败');
      // 设置为空对象以防止错误
      setSupportedCountries({
        countries: [],
        continentGroups: {},
        stats: { totalCountries: 0, continents: 0, continentBreakdown: {} }
      });
    }
  };

  // 获取地理位置统计
  const fetchLocationStats = async (days = 30) => {
    try {
      const response = await API.get(`/api/admin/risk-management/location-stats?days=${days}`);
      const stats = response.data.stats || response.data;
      setLocationStats({
        ...stats,
        topLocations: response.data.topLocations || [],
        topCountries: response.data.topCountries || [],
        topContinents: response.data.topContinents || [],
        unsupportedLocations: response.data.unsupportedLocations || [],
        analysisWindow: response.data.analysisWindow || `${days}天`
      });
    } catch (error) {
      console.error('获取地理位置统计失败:', error);
      message.error('获取地理位置统计失败');
      setLocationStats(null);
    }
  };

  // 测试地理位置解析
  const testLocationParsing = async (location) => {
    try {
      const response = await API.post('/api/admin/risk-management/test-location', { location });
      return response.data;
    } catch (error) {
      console.error('测试地理位置解析失败:', error);
      throw error;
    }
  };

  // 批量测试地理位置
  const batchTestLocations = async (locations) => {
    try {
      const response = await API.post('/api/admin/risk-management/test-locations-batch', { locations });
      return response.data;
    } catch (error) {
      console.error('批量测试地理位置失败:', error);
      throw error;
    }
  };

  // 测试用户风险评估
  const testUserRiskAssessment = async (userId, testData = {}) => {
    try {
      const response = await API.post('/api/admin/risk-management/test-assessment', {
        userId,
        ip: testData.ip || '127.0.0.1',
        location: testData.location || '本地网络',
        deviceInfo: testData.deviceInfo || {}
      });
      return response.data;
    } catch (error) {
      console.error('用户风险评估测试失败:', error);
      throw error;
    }
  };

  // 重新评估用户风险 (预留功能)
  // const reassessUserRisk = async (userId) => {
  //   try {
  //     const response = await API.post(`/api/admin/risk-management/reassess-user/${userId}`);
  //     return response.data;
  //   } catch (error) {
  //     console.error('重新评估用户风险失败:', error);
  //     throw error;
  //   }
  // };

  // 获取用户风险统计详情 (预留功能)
  // const getUserRiskStats = async (userId, days = 30) => {
  //   try {
  //     const response = await API.get(`/api/admin/risk-management/user-risk-stats/${userId}?days=${days}`);
  //     return response.data;
  //   } catch (error) {
  //     console.error('获取用户风险统计失败:', error);
  //     throw error;
  //   }
  // };

  // 添加新的国家模式
  const addCountryPattern = async (pattern, country, continent) => {
    try {
      const response = await API.post('/api/admin/risk-management/countries/add-pattern', {
        pattern,
        country,
        continent
      });
      return response.data;
    } catch (error) {
      console.error('添加国家模式失败:', error);
      throw error;
    }
  };

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      await fetchRiskConfig();
      await fetchSystemStats();
      await fetchUserRiskStats();
      await fetchRiskLogs();
      await fetchSupportedCountries();
      await fetchLocationStats();
      
      // 加载新的统计数据
      const logStats = await fetchLogStatistics();
      if (logStats) {
        setLogStatistics(logStats);
      }
      
      const trendData = await fetchRiskTrend();
      if (trendData) {
        setRiskTrend(trendData);
      }
    };
    initData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 获取风险等级配置
  const getRiskLevelConfig = (level) => {
    const configs = {
      'low': { text: '低风险', color: '#52c41a', bgColor: '#f6ffed' },
      'medium': { text: '中等风险', color: '#faad14', bgColor: '#fff7e6' },
      'high': { text: '高风险', color: '#ff7a45', bgColor: '#fff2e8' },
      'critical': { text: '严重风险', color: '#ff4d4f', bgColor: '#fff1f0' }
    };
    return configs[level] || { text: '未知', color: '#8c8c8c', bgColor: '#f5f5f5' };
  };

  // 测试风险评估
  const handleTestAssessment = async (values) => {
    try {
      const response = await API.post('/api/admin/risk-management/test-assessment', values);
      
      Modal.success({
        title: '风险评估测试结果',
        width: 600,
        content: (
          <div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="风险分数">
                <Progress 
                  percent={response.data.riskScore} 
                  strokeColor={getRiskLevelConfig(response.data.riskLevel).color}
                  format={percent => `${percent}分`}
                />
              </Descriptions.Item>
              <Descriptions.Item label="风险等级">
                <Tag color={getRiskLevelConfig(response.data.riskLevel).color}>
                  {getRiskLevelConfig(response.data.riskLevel).text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="建议操作">
                {response.data.recommendation}
              </Descriptions.Item>
              <Descriptions.Item label="评估因子">
                <div>
                  {response.data.factors?.map((factor, index) => (
                    <Tag key={index} style={{ marginBottom: 4 }}>
                      {factor.name}: {factor.score}分 ({factor.weight}权重)
                    </Tag>
                  ))}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )
      });
      
      setTestModalVisible(false);
      testForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.message || '测试失败');
    }
  };

  // 更新风险配置
  const handleUpdateConfig = async (values) => {
    try {
      console.log('表单提交的值:', values);
      
      // 根据新接口格式构造完整配置数据，添加默认值处理
      const configData = {
        threshold: values.threshold || 60,
        weights: {
          LOCATION_DISTANCE: values.LOCATION_DISTANCE || 30,
          DEVICE_FAMILIARITY: values.DEVICE_FAMILIARITY || 25,
          TIME_PATTERN: values.TIME_PATTERN || 20,
          LOGIN_FREQUENCY: values.LOGIN_FREQUENCY || 15,
          IP_REPUTATION: values.IP_REPUTATION || 10
        },
        locationConfig: {
          FAMILIAR_THRESHOLD_KM: values.FAMILIAR_THRESHOLD_KM || 100,
          FREQUENT_LOCATION_RATIO: values.FREQUENT_LOCATION_RATIO || 0.3,
          NEARBY_LOCATION_RATIO: values.NEARBY_LOCATION_RATIO || 0.2
        },
        deviceConfig: {
          SIMILARITY_THRESHOLDS: {
            IDENTICAL: values.IDENTICAL || 0.95,
            VERY_SIMILAR: values.VERY_SIMILAR || 0.85,
            SIMILAR: values.SIMILAR || 0.7,
            SOMEWHAT_SIMILAR: values.SOMEWHAT_SIMILAR || 0.5
          }
        },
        timePatternConfig: {
          WINDOW_HOURS: values.WINDOW_HOURS || 3,
          MIN_HISTORY_COUNT: values.MIN_HISTORY_COUNT || 5,
          FREQUENT_THRESHOLD: values.FREQUENT_THRESHOLD || 0.3,
          OCCASIONAL_THRESHOLD: values.OCCASIONAL_THRESHOLD || 0.15
        },
        frequencyConfig: {
          MAX_LOGINS_24H: values.MAX_LOGINS_24H || 10,
          MAX_LOGINS_1H: values.MAX_LOGINS_1H || 3,
          SUSPICIOUS_FREQUENCY_SCORE: values.SUSPICIOUS_FREQUENCY_SCORE || 80,
          HIGH_FREQUENCY_SCORE: values.HIGH_FREQUENCY_SCORE || 70
        },
        ipReputationConfig: {
          LOCAL_IP_SCORE: values.LOCAL_IP_SCORE || 5,
          SUSPICIOUS_IP_SCORE: values.SUSPICIOUS_IP_SCORE || 60,
          NORMAL_IP_SCORE: values.NORMAL_IP_SCORE || 20
        },
        analysisDays: values.ANALYSIS_DAYS || 30,
        specialRules: {
          NEW_USER_DAYS: values.NEW_USER_DAYS || 7,
          NEW_USER_THRESHOLD_ADJUSTMENT: values.NEW_USER_THRESHOLD_ADJUSTMENT || -10,
          VIP_THRESHOLD_ADJUSTMENT: values.VIP_THRESHOLD_ADJUSTMENT || -5,
          ADMIN_THRESHOLD_ADJUSTMENT: values.ADMIN_THRESHOLD_ADJUSTMENT || 5,
          FAILED_LOGIN_ADJUSTMENT: values.FAILED_LOGIN_ADJUSTMENT || 15
        }
      };
      
      console.log('发送到后端的配置数据:', configData);
      
      await API.put('/api/admin/risk-management/config', configData);
      message.success('配置更新成功');
      setConfigModalVisible(false);
      fetchRiskConfig();
    } catch (error) {
      console.error('配置更新失败:', error);
      message.error(error.response?.data?.message || '配置更新失败');
    }
  };


  // 搜索风险日志
  const handleSearch = (values) => {
    const params = {};
    if (values.username) params.username = values.username;
    if (values.userId) params.userId = values.userId;
    if (values.riskLevel) params.riskLevel = values.riskLevel;
    if (values.action) params.action = values.action;
    if (values.ipAddress) params.ipAddress = values.ipAddress;
    if (values.dateRange && values.dateRange.length === 2) {
      params.startDate = values.dateRange[0].format('YYYY-MM-DD');
      params.endDate = values.dateRange[1].format('YYYY-MM-DD');
    }
    fetchRiskLogs(params);
  };

  // 导出风险报告
  const handleExportReport = () => {
    try {
      const csvContent = exportRiskDataToCSV(riskLogs);
      downloadCSV(csvContent, `risk_assessment_report_${dayjs().format('YYYY-MM-DD')}.csv`);
      message.success('报告导出成功');
    } catch (error) {
      message.error('导出失败');
    }
  };

  // 处理地理位置测试
  const handleLocationTest = async (values) => {
    try {
      if (values.batchTest && values.locations) {
        // 批量测试
        const locations = values.locations.split('\n').filter(loc => loc.trim());
        const result = await batchTestLocations(locations);
        
        Modal.success({
          title: '批量地理位置测试结果',
          width: 800,
          content: (
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <Table
                size="small"
                columns={[
                  { title: '原始位置', dataIndex: 'original', key: 'original' },
                  { title: '解析结果', dataIndex: 'parsed', key: 'parsed' },
                  { title: '国家', dataIndex: 'country', key: 'country' },
                  { title: '大洲', dataIndex: 'continent', key: 'continent' },
                  { 
                    title: '状态', 
                    dataIndex: 'success', 
                    key: 'success',
                    render: (success) => (
                      <Tag color={success ? 'green' : 'red'}>
                        {success ? '成功' : '失败'}
                      </Tag>
                    )
                  }
                ]}
                dataSource={result.results || []}
                pagination={false}
                rowKey={(record, index) => index}
              />
            </div>
          )
        });
      } else {
        // 单个测试
        const result = await testLocationParsing(values.location);
        
        Modal.success({
          title: '地理位置测试结果',
          content: (
            <Descriptions column={1} bordered>
              <Descriptions.Item label="原始位置">{values.location}</Descriptions.Item>
              <Descriptions.Item label="解析结果">{result.parsed}</Descriptions.Item>
              <Descriptions.Item label="国家">{result.country}</Descriptions.Item>
              <Descriptions.Item label="大洲">{result.continent}</Descriptions.Item>
              <Descriptions.Item label="置信度">{result.confidence}%</Descriptions.Item>
            </Descriptions>
          )
        });
      }
      
      setLocationModalVisible(false);
      locationForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.message || '测试失败');
    }
  };

  // 用户风险测试处理
  const handleUserRiskTest = async (values) => {
    try {
      const result = await testUserRiskAssessment(values.userId, {
        ip: values.ip,
        location: values.location,
        deviceInfo: values.deviceInfo ? JSON.parse(values.deviceInfo) : {}
      });
      
      Modal.success({
        title: '用户风险评估结果',
        content: (
          <div>
            <Descriptions column={1} bordered style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="用户">{result.user?.username} ({result.user?.email})</Descriptions.Item>
              <Descriptions.Item label="风险等级">{result.riskAssessment?.riskLevel}</Descriptions.Item>
              <Descriptions.Item label="风险分数">{result.riskAssessment?.riskScore}</Descriptions.Item>
              <Descriptions.Item label="是否需要验证">{result.riskAssessment?.isRisky ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="建议">{result.riskAssessment?.recommendation}</Descriptions.Item>
            </Descriptions>
            
            {result.riskAssessment?.riskFactors && (
              <div>
                <Title level={5}>风险因子详情</Title>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="地理位置风险">{result.riskAssessment.riskFactors.locationRisk}</Descriptions.Item>
                  <Descriptions.Item label="设备风险">{result.riskAssessment.riskFactors.deviceRisk}</Descriptions.Item>
                  <Descriptions.Item label="时间模式风险">{result.riskAssessment.riskFactors.timeRisk}</Descriptions.Item>
                  <Descriptions.Item label="频率风险">{result.riskAssessment.riskFactors.frequencyRisk}</Descriptions.Item>
                  <Descriptions.Item label="IP风险">{result.riskAssessment.riskFactors.ipRisk}</Descriptions.Item>
                </Descriptions>
              </div>
            )}
          </div>
        ),
        width: 800
      });
      
      userTestForm.resetFields();
      setUserTestModalVisible(false);
    } catch (error) {
      message.error(error.response?.data?.message || '测试失败');
    }
  };

  // 处理添加国家模式
  const handleAddCountryPattern = async (values) => {
    try {
      const result = await addCountryPattern(values.pattern, values.country, values.continent);
      message.success(`国家模式添加成功: ${result.pattern?.country || values.country}`);
      setCountryModalVisible(false);
      countryForm.resetFields();
      fetchSupportedCountries(); // 刷新国家列表
    } catch (error) {
      message.error(error.response?.data?.message || '添加失败');
    }
  };

  // 查看日志详情
  const handleViewLogDetail = async (logId) => {
    try {
      const logDetail = await fetchLogDetail(logId);
      if (logDetail) {
        setSelectedRiskLog(logDetail);
        setLogDetailModalVisible(true);
      }
    } catch (error) {
      console.error('获取日志详情失败:', error);
      message.error('获取日志详情失败');
    }
  };

  // 清理过期日志
  const handleCleanupLogs = () => {
    Modal.confirm({
      title: '确认清理过期日志',
      content: '此操作将删除90天前的所有日志记录，是否继续？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await cleanupLogs(90);
          message.success(`日志清理完成，删除了 ${result.deletedCount} 条记录`);
          // 重新加载日志数据
          await fetchRiskLogs();
        } catch (error) {
          console.error('清理日志失败:', error);
          message.error('清理日志失败');
        }
      }
    });
  };

  // 重置配置为默认值
  const handleResetConfig = () => {
    Modal.confirm({
      title: '确认重置配置',
      content: '此操作将把所有风险评估配置重置为系统默认值，是否继续？',
      okText: '确认重置',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await resetConfigToDefault();
          message.success(result.message || '配置重置成功');
          // 重新加载配置
          await fetchRiskConfig();
          setConfigModalVisible(false);
        } catch (error) {
          console.error('重置配置失败:', error);
          message.error('重置配置失败');
        }
      }
    });
  };

  // 查看配置历史
  const handleViewConfigHistory = async () => {
    try {
      const history = await fetchConfigHistory();
      setConfigHistory(history);
      setConfigHistoryVisible(true);
    } catch (error) {
      console.error('获取配置历史失败:', error);
      message.error('获取配置历史失败');
    }
  };

  // 查看缓存统计
  const handleViewCacheStats = async () => {
    try {
      const stats = await fetchCacheStats();
      setCacheStats(stats);
      setCacheStatsVisible(true);
    } catch (error) {
      console.error('获取缓存统计失败:', error);
      message.error('获取缓存统计失败');
    }
  };

  // 清除配置缓存
  const handleClearCache = () => {
    Modal.confirm({
      title: '确认清除缓存',
      content: '此操作将清除所有配置缓存，强制重新从数据库加载配置，是否继续？',
      okText: '确认清除',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await clearConfigCache();
          message.success(result.message || '缓存清除成功');
          // 重新加载配置
          await fetchRiskConfig();
        } catch (error) {
          console.error('清除缓存失败:', error);
          message.error('清除缓存失败');
        }
      }
    });
  };

  // 导出CSV数据
  const exportRiskDataToCSV = (data) => {
    const headers = ['时间', '用户名', '账号类型', '风险等级', '风险分数', '登录位置', '设备信息', '处理结果', 'IP地址'];
    const csvContent = [
      headers.join(','),
      ...data.map(log => {
        // 构建设备信息字符串
        const deviceParts = [];
        if (log.deviceType) deviceParts.push(log.deviceType);
        if (log.browser) deviceParts.push(log.browser);
        if (log.os) deviceParts.push(log.os);
        const deviceInfo = deviceParts.length > 0 ? deviceParts.join(' / ') : '未知设备';
        
        // 处理结果映射
        const actionMap = {
          'allow': '允许登录',
          'require_verification': '需要验证',
          'block': '阻止登录'
        };
        const actionText = actionMap[log.action] || log.action || '未知';
        
        return [
          log.assessmentTime || log.timestamp,
          log.username,
          log.accountType === 'main' ? '主账号' : 'RAM账号',
          getRiskLevelConfig(log.riskLevel).text,
          `${log.riskScore}分`,
          log.location || '',
          deviceInfo,
          actionText,
          log.ipAddress || log.ip || ''
        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      })
    ].join('\n');
    return csvContent;
  };

  // 下载CSV文件
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // 风险日志表格列
  const riskLogColumns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 120
    },
    {
      title: '账号类型',
      dataIndex: 'accountType',
      key: 'accountType',
      width: 100,
      render: (type) => (
        <Tag color={type === 'main' ? 'blue' : 'green'}>
          {type === 'main' ? '主账号' : 'RAM账号'}
        </Tag>
      )
    },
    {
      title: '风险等级',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 100,
      render: (level) => {
        const config = getRiskLevelConfig(level);
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '风险分数',
      dataIndex: 'riskScore',
      key: 'riskScore',
      width: 100,
      render: (score) => `${score}分`
    },
    {
      title: '登录位置',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      ellipsis: true
    },
    {
      title: '设备信息',
      key: 'deviceInfo',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const deviceParts = [];
        if (record.deviceType) deviceParts.push(record.deviceType);
        if (record.browser) deviceParts.push(record.browser);
        if (record.os) deviceParts.push(record.os);
        return deviceParts.length > 0 ? deviceParts.join(' / ') : '未知设备';
      }
    },
    {
      title: '处理结果',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (action, record) => {
        const resultConfig = {
          'allow': { text: '允许登录', color: 'green' },
          'require_verification': { text: '需要验证', color: 'orange' },
          'block': { text: '阻止登录', color: 'red' }
        };
        
        // 如果有验证相关信息，显示更详细的状态
        if (action === 'require_verification' && record.verificationPassed !== null) {
          if (record.verificationPassed) {
            return <Tag color="blue">验证通过</Tag>;
          } else {
            return <Tag color="red">验证失败</Tag>;
          }
        }
        
        const config = resultConfig[action] || { text: action || '未知', color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'operations',
      width: 100,
      render: (_, record) => (
        <Button 
          type="link" 
          size="small" 
          icon={<EyeOutlined />}
          onClick={() => handleViewLogDetail(record.id)}
        >
          完整详情
        </Button>
      )
    }
  ];

  // 转换风险趋势数据为图表格式
  const getRiskTrendData = () => {
    if (!riskTrend || riskTrend.length === 0) return [];
    
    // 将每个日期的数据转换为多个数据点
    const trendData = [];
    riskTrend.forEach(item => {
      trendData.push({
        date: item.date,
        type: '总评估数',
        count: item.totalCount,
        color: '#1890ff'
      });
      trendData.push({
        date: item.date,
        type: '风险评估数',
        count: item.riskyCount,
        color: '#ff4d4f'
      });
    });
    return trendData;
  };

  // 系统统计图表配置
  const riskTrendConfig = {
    data: getRiskTrendData(),
    xField: 'date',
    yField: 'count',
    seriesField: 'type',
    color: ['#1890ff', '#ff4d4f'],
    legend: {
      position: 'top'
    },
    smooth: true,
    point: {
      size: 5,
      shape: 'diamond',
    },
    tooltip: {
      formatter: (datum) => {
        const typeMap = {
          '总评估数': '总评估数',
          '风险评估数': '风险评估数'
        };
        return {
          name: typeMap[datum.type] || datum.type,
          value: `${datum.count} 次`
        };
      }
    },
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
  };

  // 转换风险评估数据为图表格式
  const getRiskDistributionData = () => {
    if (!systemStats?.riskAssessments) return [];
    
    const riskLevelMap = {
      'low': { name: '低风险', color: '#52c41a' },
      'medium': { name: '中等风险', color: '#faad14' },
      'high': { name: '高风险', color: '#ff7a45' },
      'critical': { name: '严重风险', color: '#ff4d4f' }
    };
    
    return Object.entries(systemStats.riskAssessments)
      .filter(([_, count]) => count > 0)
      .map(([level, count]) => ({
        riskLevel: level,
        name: riskLevelMap[level]?.name || level,
        count: count,
        color: riskLevelMap[level]?.color
      }));
  };

  const riskDistributionConfig = {
    data: getRiskDistributionData(),
    angleField: 'count',
    colorField: 'name',
    color: ['#52c41a', '#faad14', '#ff7a45', '#ff4d4f'],
    radius: 0.8,
    legend: {
      position: 'bottom',
    },
    tooltip: {
      showTitle: true,
      showMarkers: true,
    },
    statistic: {
      title: false,
      content: {
        style: {
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        content: '风险分布',
      },
    },
    interactions: [
      {
        type: 'element-active',
      },
      {
        type: 'pie-statistic-active',
      },
    ],
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <SecurityScanOutlined style={{ marginRight: '8px' }} />
          异地登录风险管理
        </Title>
        <Text type="secondary">
          监控和管理系统的异地登录风险评估，保障账户安全
        </Text>
      </div>

      {/* 统计概览 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="总登录次数"
              value={systemStats?.totalLogins || 0}
              prefix={<SecurityScanOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix={systemStats?.analysisWindow ? `(${systemStats.analysisWindow})` : ''}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="高风险登录"
              value={(systemStats?.riskAssessments?.high || 0) + (systemStats?.riskAssessments?.critical || 0)}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="需要验证"
              value={systemStats?.verificationRequired || 0}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="低风险登录"
              value={systemStats?.riskAssessments?.low || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="风险趋势分析" extra={
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates);
                if (dates && dates.length === 2) {
                  fetchSystemStats(dates[1].diff(dates[0], 'day'));
                }
              }}
            />
          }>
            {getRiskTrendData().length > 0 ? (
              <div>
                <Line {...riskTrendConfig} height={250} />
                <div style={{ marginTop: '16px', fontSize: '12px' }}>
                  {riskTrend && riskTrend.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>最近数据:</div>
                      {riskTrend.slice(-3).map((item, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          padding: '2px 0',
                          borderBottom: index < 2 ? '1px solid #f0f0f0' : 'none'
                        }}>
                          <span>{item.date}</span>
                          <span>
                            总评估: <strong style={{ color: '#1890ff' }}>{item.totalCount}</strong> | 
                            风险评估: <strong style={{ color: '#ff4d4f' }}>{item.riskyCount}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Empty description="暂无趋势数据" style={{ padding: '60px 0' }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="风险等级分布">
            {getRiskDistributionData().length > 0 ? (
              <div>
                <Pie {...riskDistributionConfig} height={200} />
                <div style={{ marginTop: '16px' }}>
                  {getRiskDistributionData().map((item, index) => {
                    const total = getRiskDistributionData().reduce((sum, d) => sum + d.count, 0);
                    const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: index < getRiskDistributionData().length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: item.color,
                            borderRadius: '2px',
                            marginRight: '8px'
                          }}></div>
                          <span style={{ fontSize: '14px' }}>{item.name}</span>
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                          {item.count} 次 ({percentage}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <Empty description="暂无分布数据" style={{ padding: '60px 0' }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* 风险因子统计 */}
      {systemStats?.topRiskFactors && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Card title="风险因子触发统计">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="地理位置风险"
                    value={systemStats.topRiskFactors.location || 0}
                    prefix={<EnvironmentOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="设备风险"
                    value={systemStats.topRiskFactors.device || 0}
                    prefix={<SecurityScanOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="时间模式风险"
                    value={systemStats.topRiskFactors.time || 0}
                    prefix={<ExclamationCircleOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="登录频率风险"
                    value={systemStats.topRiskFactors.frequency || 0}
                    prefix={<ReloadOutlined />}
                    valueStyle={{ color: '#ff7a45' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <Statistic
                    title="IP信誉风险"
                    value={systemStats.topRiskFactors.ip || 0}
                    prefix={<WarningOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Col>
              </Row>
              {systemStats.analysisWindow && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <Text type="secondary">
                    统计周期: {systemStats.analysisWindow} | 
                    更新时间: {systemStats.timestamp ? dayjs(systemStats.timestamp).format('YYYY-MM-DD HH:mm:ss') : '未知'}
                  </Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* 日志统计面板 */}
      {logStatistics && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Card title={`评估日志统计 (${logStatistics.period || '7天'})`}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="总评估次数"
                    value={logStatistics.totalAssessments || 0}
                    prefix={<SecurityScanOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="风险评估次数"
                    value={logStatistics.riskyCounts || 0}
                    prefix={<WarningOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="平均风险分数"
                    value={logStatistics.averageRiskScore || 0}
                    precision={1}
                    suffix="分"
                    prefix={<ExclamationCircleOutlined />}
                    valueStyle={{ 
                      color: (logStatistics.averageRiskScore >= 60) ? '#ff4d4f' : 
                             (logStatistics.averageRiskScore >= 30) ? '#faad14' : '#52c41a' 
                    }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="平均处理时间"
                    value={logStatistics.averageProcessingTime || 0}
                    precision={1}
                    suffix="ms"
                    prefix={<ReloadOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
              
              {/* 风险等级分布 */}
              {logStatistics.riskLevelBreakdown && (
                <div style={{ marginTop: '16px' }}>
                  <Title level={5}>风险等级分布</Title>
                  <Row gutter={[16, 8]}>
                    {Object.entries(logStatistics.riskLevelBreakdown).map(([level, count]) => {
                      const levelConfig = {
                        'low': { name: '低风险', color: '#52c41a' },
                        'medium': { name: '中等风险', color: '#faad14' },
                        'high': { name: '高风险', color: '#ff7a45' },
                        'critical': { name: '严重风险', color: '#ff4d4f' }
                      };
                      const config = levelConfig[level] || { name: level, color: '#1890ff' };
                      
                      return (
                        <Col xs={12} sm={8} md={6} key={level}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                              fontSize: '24px', 
                              fontWeight: 'bold', 
                              color: config.color 
                            }}>
                              {count}
                            </div>
                            <div style={{ color: '#666', fontSize: '12px' }}>
                              {config.name}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
              
              {/* 处理动作分布 */}
              {logStatistics.actionBreakdown && (
                <div style={{ marginTop: '16px' }}>
                  <Title level={5}>处理动作分布</Title>
                  <Row gutter={[16, 8]}>
                    {Object.entries(logStatistics.actionBreakdown).map(([action, count]) => {
                      const actionConfig = {
                        'allow': { name: '允许登录', color: '#52c41a' },
                        'require_verification': { name: '需要验证', color: '#faad14' },
                        'block': { name: '阻止登录', color: '#ff4d4f' }
                      };
                      const config = actionConfig[action] || { name: action, color: '#1890ff' };
                      
                      return (
                        <Col xs={12} sm={8} md={6} key={action}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                              fontSize: '24px', 
                              fontWeight: 'bold', 
                              color: config.color 
                            }}>
                              {count}
                            </div>
                            <div style={{ color: '#666', fontSize: '12px' }}>
                              {config.name}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* 风险趋势详情 */}
      {riskTrend && riskTrend.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={24}>
            <Card title={`风险趋势详情 (${riskTrend[0]?.period || '7天'})`}>
              <Row gutter={[16, 16]}>
                {riskTrend.map((item, index) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={index}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                        {item.date}
                      </div>
                      <Row gutter={8}>
                        <Col span={12}>
                          <Statistic
                            title="总评估"
                            value={item.totalCount}
                            valueStyle={{ fontSize: '16px', color: '#1890ff' }}
                          />
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="风险评估"
                            value={item.riskyCount}
                            valueStyle={{ fontSize: '16px', color: '#ff4d4f' }}
                          />
                        </Col>
                      </Row>
                      <Row gutter={8} style={{ marginTop: '8px' }}>
                        <Col span={12}>
                          <Statistic
                            title="风险率"
                            value={item.riskRate}
                            suffix="%"
                            valueStyle={{ fontSize: '14px', color: '#faad14' }}
                          />
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="平均分数"
                            value={item.averageRiskScore}
                            precision={1}
                            valueStyle={{ 
                              fontSize: '14px', 
                              color: item.averageRiskScore >= 60 ? '#ff4d4f' : 
                                     item.averageRiskScore >= 30 ? '#faad14' : '#52c41a' 
                            }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* 搜索和操作区域 */}
      <Card style={{ marginBottom: '16px' }}>
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: '16px' }}
        >
          <Form.Item name="username">
            <Input 
              placeholder="用户名" 
              prefix={<UserOutlined />}
              style={{ width: 150 }}
            />
          </Form.Item>
          <Form.Item name="userId">
            <Input 
              placeholder="用户ID" 
              style={{ width: 150 }}
            />
          </Form.Item>
          <Form.Item name="riskLevel">
            <Select placeholder="风险等级" style={{ width: 120 }}>
              <Option value="">全部</Option>
              <Option value="low">低风险</Option>
              <Option value="medium">中等风险</Option>
              <Option value="high">高风险</Option>
              <Option value="critical">严重风险</Option>
            </Select>
          </Form.Item>
          <Form.Item name="action">
            <Select placeholder="处理动作" style={{ width: 120 }}>
              <Option value="">全部</Option>
              <Option value="allow">允许</Option>
              <Option value="require_verification">需要验证</Option>
              <Option value="block">阻止</Option>
            </Select>
          </Form.Item>
          <Form.Item name="ipAddress">
            <Input 
              placeholder="IP地址" 
              style={{ width: 150 }}
            />
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              搜索
            </Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={() => {
              searchForm.resetFields();
              fetchRiskLogs();
            }}>
              重置
            </Button>
          </Form.Item>
        </Form>

        <Space wrap>
          <Button 
            type="primary" 
            icon={<SettingOutlined />}
            onClick={() => setConfigModalVisible(true)}
          >
            风险配置
          </Button>
          <Button 
            icon={<ExclamationCircleOutlined />}
            onClick={() => setTestModalVisible(true)}
          >
            测试评估
          </Button>
          <Button 
            icon={<EnvironmentOutlined />}
            onClick={() => setLocationModalVisible(true)}
          >
            地理位置测试
          </Button>
          <Button 
            icon={<SecurityScanOutlined />}
            onClick={() => setCountryModalVisible(true)}
          >
            国家管理
          </Button>
          <Button 
            icon={<UserOutlined />}
            onClick={() => setUserTestModalVisible(true)}
          >
            用户风险测试
          </Button>
          <Button 
            icon={<DownloadOutlined />}
            onClick={handleExportReport}
            disabled={riskLogs.length === 0}
          >
            导出报告
          </Button>
          <Button 
            icon={<DeleteOutlined />}
            onClick={handleCleanupLogs}
            danger
          >
            清理过期日志
          </Button>
          <Button 
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchRiskLogs();
              fetchSystemStats();
              fetchUserRiskStats();
              fetchSupportedCountries();
              fetchLocationStats();
            }}
          >
            刷新数据
          </Button>
        </Space>
      </Card>

      {/* 风险日志表格 */}
      <Card title="风险评估日志">
        <Table
          columns={riskLogColumns}
          dataSource={riskLogs}
          loading={loading}
          rowKey={(record, index) => record.id || index}
          pagination={{
            total: riskLogs.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 测试评估弹窗 */}
      <Modal
        title="测试风险评估"
        open={testModalVisible}
        onCancel={() => {
          setTestModalVisible(false);
          testForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={testForm}
          onFinish={handleTestAssessment}
          layout="vertical"
        >
          <Form.Item
            name="userId"
            label="用户ID"
            rules={[{ required: true, message: '请输入用户ID' }]}
          >
            <Input placeholder="请输入要测试的用户ID" />
          </Form.Item>
          <Form.Item
            name="ip"
            label="IP地址"
            rules={[{ required: true, message: '请输入IP地址' }]}
          >
            <Input placeholder="请输入测试IP地址" />
          </Form.Item>
          <Form.Item
            name="location"
            label="地理位置"
            rules={[{ required: true, message: '请输入地理位置' }]}
          >
            <Input placeholder="例如：中国广东深圳" />
          </Form.Item>
          <Form.Item
            name="deviceInfo"
            label="设备信息"
          >
            <Input.TextArea 
              placeholder="请输入设备信息JSON（可选）"
              rows={4}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                开始测试
              </Button>
              <Button onClick={() => {
                setTestModalVisible(false);
                testForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 风险配置弹窗 */}
      <Modal
        title="风险评估配置"
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        footer={null}
        width={1000}
      >
        <Form
          form={configForm}
          onFinish={handleUpdateConfig}
          layout="vertical"
        >
          <Tabs
            defaultActiveKey="basic"
            items={[
              {
                key: 'basic',
                label: '基础配置',
                children: (
                  <div>
                    <Alert
                      message="基础配置说明"
                      description="风险阈值用于判断是否需要进行身份验证，分数越高风险越大。"
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="threshold"
                          label="风险阈值"
                          rules={[{ required: true, message: '请输入风险阈值' }]}
                        >
                          <Slider min={0} max={100} marks={{ 0: '0', 30: '30', 60: '60', 80: '80', 100: '100' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="ANALYSIS_DAYS"
                          label="分析天数"
                          rules={[{ required: true, message: '请输入分析天数' }]}
                          initialValue={30}
                        >
                          <Input type="number" min={1} max={365} suffix="天" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'weights',
                label: '权重配置',
                children: (
                  <div>
                    <Alert
                      message="权重配置说明"
                      description="各风险因子的权重，总和应为100。权重越高，该因子对最终风险分数的影响越大。"
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="LOCATION_DISTANCE"
                          label="地理位置权重"
                          rules={[{ required: true, message: '请输入地理位置权重' }]}
                        >
                          <Slider min={0} max={100} marks={{ 0: '0%', 50: '50%', 100: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="DEVICE_FAMILIARITY"
                          label="设备熟悉度权重"
                          rules={[{ required: true, message: '请输入设备熟悉度权重' }]}
                        >
                          <Slider min={0} max={100} marks={{ 0: '0%', 50: '50%', 100: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="TIME_PATTERN"
                          label="时间模式权重"
                          rules={[{ required: true, message: '请输入时间模式权重' }]}
                        >
                          <Slider min={0} max={100} marks={{ 0: '0%', 50: '50%', 100: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="LOGIN_FREQUENCY"
                          label="登录频率权重"
                          rules={[{ required: true, message: '请输入登录频率权重' }]}
                        >
                          <Slider min={0} max={100} marks={{ 0: '0%', 50: '50%', 100: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="IP_REPUTATION"
                          label="IP信誉权重"
                          rules={[{ required: true, message: '请输入IP信誉权重' }]}
                        >
                          <Slider min={0} max={100} marks={{ 0: '0%', 50: '50%', 100: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'location',
                label: '地理位置配置',
                children: (
                  <div>
                    <Alert
                      message="地理位置配置说明"
                      description="配置地理位置相关的风险评估参数。"
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                    
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          name="FAMILIAR_THRESHOLD_KM"
                          label="熟悉位置阈值 (公里)"
                          rules={[{ required: true, message: '请输入熟悉位置阈值' }]}
                          initialValue={100}
                        >
                          <Input type="number" min={0} suffix="km" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="FREQUENT_LOCATION_RATIO"
                          label="常用位置比例"
                          rules={[{ required: true, message: '请输入常用位置比例' }]}
                          initialValue={0.3}
                        >
                          <Input type="number" min={0} max={1} step={0.1} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="NEARBY_LOCATION_RATIO"
                          label="附近位置比例"
                          rules={[{ required: true, message: '请输入附近位置比例' }]}
                          initialValue={0.2}
                        >
                          <Input type="number" min={0} max={1} step={0.1} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'device',
                label: '设备配置',
                children: (
                  <div>
                    <Alert
                      message="设备配置说明"
                      description="配置设备相似度阈值，用于判断设备的熟悉程度。"
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="IDENTICAL"
                          label="完全相同阈值"
                          rules={[{ required: true, message: '请输入完全相同阈值' }]}
                        >
                          <Input type="number" min={0} max={1} step={0.01} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="VERY_SIMILAR"
                          label="非常相似阈值"
                          rules={[{ required: true, message: '请输入非常相似阈值' }]}
                        >
                          <Input type="number" min={0} max={1} step={0.01} />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="SIMILAR"
                          label="相似阈值"
                          rules={[{ required: true, message: '请输入相似阈值' }]}
                        >
                          <Input type="number" min={0} max={1} step={0.01} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="SOMEWHAT_SIMILAR"
                          label="有些相似阈值"
                          rules={[{ required: true, message: '请输入有些相似阈值' }]}
                        >
                          <Input type="number" min={0} max={1} step={0.01} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'advanced',
                label: '高级配置',
                children: (
                  <div>
                    <Alert
                      message="高级配置说明"
                      description="配置时间模式、登录频率、IP信誉等高级参数。"
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                    
                    <Tabs
                      size="small"
                      items={[
                        {
                          key: 'time',
                          label: '时间模式',
                          children: (
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item name="WINDOW_HOURS" label="时间窗口 (小时)">
                                  <Input type="number" min={1} max={24} />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="MIN_HISTORY_COUNT" label="最小历史记录数">
                                  <Input type="number" min={1} />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="FREQUENT_THRESHOLD" label="频繁阈值">
                                  <Input type="number" min={0} max={1} step={0.01} />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="OCCASIONAL_THRESHOLD" label="偶尔阈值">
                                  <Input type="number" min={0} max={1} step={0.01} />
                                </Form.Item>
                              </Col>
                            </Row>
                          )
                        },
                        {
                          key: 'frequency',
                          label: '登录频率',
                          children: (
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item name="MAX_LOGINS_24H" label="24小时最大登录次数">
                                  <Input type="number" min={1} />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="MAX_LOGINS_1H" label="1小时最大登录次数">
                                  <Input type="number" min={1} />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="SUSPICIOUS_FREQUENCY_SCORE" label="可疑频率分数">
                                  <Input type="number" min={0} max={100} />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="HIGH_FREQUENCY_SCORE" label="高频率分数">
                                  <Input type="number" min={0} max={100} />
                                </Form.Item>
                              </Col>
                            </Row>
                          )
                        },
                        {
                          key: 'ip',
                          label: 'IP信誉',
                          children: (
                            <Row gutter={16}>
                              <Col span={8}>
                                <Form.Item name="LOCAL_IP_SCORE" label="本地IP分数">
                                  <Input type="number" min={0} max={100} />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="SUSPICIOUS_IP_SCORE" label="可疑IP分数">
                                  <Input type="number" min={0} max={100} />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="NORMAL_IP_SCORE" label="普通IP分数">
                                  <Input type="number" min={0} max={100} />
                                </Form.Item>
                              </Col>
                            </Row>
                          )
                        },
                        {
                          key: 'special',
                          label: '特殊规则',
                          children: (
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item name="NEW_USER_DAYS" label="新用户天数">
                                  <Input type="number" min={1} />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="NEW_USER_THRESHOLD_ADJUSTMENT" label="新用户阈值调整">
                                  <Input type="number" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="VIP_THRESHOLD_ADJUSTMENT" label="VIP阈值调整">
                                  <Input type="number" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="ADMIN_THRESHOLD_ADJUSTMENT" label="管理员阈值调整">
                                  <Input type="number" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item name="FAILED_LOGIN_ADJUSTMENT" label="登录失败调整">
                                  <Input type="number" />
                                </Form.Item>
                              </Col>
                            </Row>
                          )
                        }
                      ]}
                    />
                  </div>
                )
              }
            ]}
          />

          <Form.Item style={{ marginTop: '24px' }}>
            <Space wrap>
              <Button type="primary" htmlType="submit">
                保存配置
              </Button>
              <Button onClick={() => setConfigModalVisible(false)}>
                取消
              </Button>
              <Button 
                onClick={handleResetConfig}
                danger
                icon={<ReloadOutlined />}
              >
                重置为默认值
              </Button>
              <Button 
                onClick={handleViewConfigHistory}
                icon={<EyeOutlined />}
              >
                配置历史
              </Button>
              <Button 
                onClick={handleViewCacheStats}
                icon={<SettingOutlined />}
              >
                缓存统计
              </Button>
              <Button 
                onClick={handleClearCache}
                icon={<DeleteOutlined />}
              >
                清除缓存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>


      {/* 完整日志详情弹窗 */}
      <Modal
        title="完整日志详情"
        open={logDetailModalVisible}
        onCancel={() => setLogDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLogDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={1000}
      >
        {selectedRiskLog && (
          <div>
            <Descriptions title="用户信息" column={2} bordered style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="用户ID">{selectedRiskLog.user?.id}</Descriptions.Item>
              <Descriptions.Item label="用户名">{selectedRiskLog.user?.username}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{selectedRiskLog.user?.email}</Descriptions.Item>
              <Descriptions.Item label="角色">{selectedRiskLog.user?.role}</Descriptions.Item>
              <Descriptions.Item label="账号类型" span={2}>
                <Tag color={selectedRiskLog.user?.accountType === 'main' ? 'blue' : 'green'}>
                  {selectedRiskLog.user?.accountType === 'main' ? '主账号' : 'RAM账号'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="登录信息" column={2} bordered style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="IP地址">{selectedRiskLog.loginInfo?.ipAddress}</Descriptions.Item>
              <Descriptions.Item label="地理位置">{selectedRiskLog.loginInfo?.location}</Descriptions.Item>
              <Descriptions.Item label="用户代理" span={2}>{selectedRiskLog.loginInfo?.userAgent}</Descriptions.Item>
              <Descriptions.Item label="设备类型">{selectedRiskLog.loginInfo?.deviceInfo?.deviceType}</Descriptions.Item>
              <Descriptions.Item label="浏览器">{selectedRiskLog.loginInfo?.deviceInfo?.browser}</Descriptions.Item>
              <Descriptions.Item label="操作系统">{selectedRiskLog.loginInfo?.deviceInfo?.os}</Descriptions.Item>
              <Descriptions.Item label="屏幕分辨率">{selectedRiskLog.loginInfo?.deviceInfo?.screen}</Descriptions.Item>
              <Descriptions.Item label="时区">{selectedRiskLog.loginInfo?.deviceInfo?.timezone}</Descriptions.Item>
              <Descriptions.Item label="语言">{selectedRiskLog.loginInfo?.deviceInfo?.language}</Descriptions.Item>
            </Descriptions>

            <Descriptions title="风险评估" column={2} bordered style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="风险等级">
                <Tag color={getRiskLevelConfig(selectedRiskLog.riskAssessment?.riskLevel).color}>
                  {getRiskLevelConfig(selectedRiskLog.riskAssessment?.riskLevel).text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="风险分数">
                <Progress 
                  percent={selectedRiskLog.riskAssessment?.riskScore} 
                  strokeColor={getRiskLevelConfig(selectedRiskLog.riskAssessment?.riskLevel).color}
                  format={percent => `${percent}分`}
                />
              </Descriptions.Item>
              <Descriptions.Item label="是否高风险">
                {selectedRiskLog.riskAssessment?.isRisky ? '是' : '否'}
              </Descriptions.Item>
              <Descriptions.Item label="建议" span={2}>
                {selectedRiskLog.riskAssessment?.recommendation}
              </Descriptions.Item>
            </Descriptions>

            {selectedRiskLog.riskAssessment?.riskFactors && (
              <Descriptions title="风险因子详情" column={1} bordered style={{ marginBottom: '16px' }}>
                <Descriptions.Item label="地理位置风险">{selectedRiskLog.riskAssessment.riskFactors.locationRisk}分</Descriptions.Item>
                <Descriptions.Item label="设备风险">{selectedRiskLog.riskAssessment.riskFactors.deviceRisk}分</Descriptions.Item>
                <Descriptions.Item label="时间模式风险">{selectedRiskLog.riskAssessment.riskFactors.timeRisk}分</Descriptions.Item>
                <Descriptions.Item label="登录频率风险">{selectedRiskLog.riskAssessment.riskFactors.frequencyRisk}分</Descriptions.Item>
                <Descriptions.Item label="IP信誉风险">{selectedRiskLog.riskAssessment.riskFactors.ipRisk}分</Descriptions.Item>
              </Descriptions>
            )}

            <Descriptions title="评估详情" column={2} bordered style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="风险阈值">{selectedRiskLog.assessmentDetails?.threshold}</Descriptions.Item>
              <Descriptions.Item label="处理时间">{selectedRiskLog.processingTime}ms</Descriptions.Item>
              <Descriptions.Item label="评估时间" span={2}>
                {dayjs(selectedRiskLog.assessmentTime).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="处理结果" column={2} bordered>
              <Descriptions.Item label="处理动作">{selectedRiskLog.result?.action}</Descriptions.Item>
              <Descriptions.Item label="验证发送">{selectedRiskLog.result?.verificationSent ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="验证通过">{selectedRiskLog.result?.verificationPassed ? '是' : '否'}</Descriptions.Item>
              <Descriptions.Item label="登录成功">{selectedRiskLog.result?.loginSuccess ? '是' : '否'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 地理位置测试弹窗 */}
      <Modal
        title="地理位置测试"
        open={locationModalVisible}
        onCancel={() => {
          setLocationModalVisible(false);
          locationForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={locationForm}
          onFinish={handleLocationTest}
          layout="vertical"
        >
          <Form.Item
            name="batchTest"
            valuePropName="checked"
          >
            <input type="checkbox" style={{ marginRight: '8px' }} />
            批量测试模式
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.batchTest !== currentValues.batchTest
            }
          >
            {({ getFieldValue }) => {
              const isBatch = getFieldValue('batchTest');
              
              if (isBatch) {
                return (
                  <Form.Item
                    name="locations"
                    label="地理位置列表（每行一个）"
                    rules={[{ required: true, message: '请输入地理位置列表' }]}
                  >
                    <Input.TextArea
                      rows={8}
                      placeholder="请输入地理位置，每行一个，例如：&#10;中国广东深圳&#10;美国加利福尼亚洛杉矶&#10;日本东京"
                    />
                  </Form.Item>
                );
              } else {
                return (
                  <Form.Item
                    name="location"
                    label="地理位置"
                    rules={[{ required: true, message: '请输入地理位置' }]}
                  >
                    <Input placeholder="例如：中国广东深圳" />
                  </Form.Item>
                );
              }
            }}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                开始测试
              </Button>
              <Button onClick={() => {
                setLocationModalVisible(false);
                locationForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 国家管理弹窗 */}
      <Modal
        title="国家管理"
        open={countryModalVisible}
        onCancel={() => setCountryModalVisible(false)}
        footer={null}
        width={1000}
      >
        <div style={{ marginBottom: '16px' }}>
          <Alert
            message="支持的国家和地区"
            description={`当前系统支持 ${supportedCountries.stats?.totalCountries || 0} 个国家和地区的地理位置解析，覆盖 ${supportedCountries.stats?.continents || 0} 个大洲`}
            type="info"
            showIcon
          />
        </div>

        {/* 统计信息 */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic 
                title="支持国家总数" 
                value={supportedCountries.stats?.totalCountries || 0} 
                prefix={<EnvironmentOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="覆盖大洲数" 
                value={supportedCountries.stats?.continents || 0} 
                suffix="个"
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="最多国家的大洲" 
                value={Object.entries(supportedCountries.stats?.continentBreakdown || {})
                  .sort(([,a], [,b]) => b - a)[0]?.[0] || '无'}
                valueStyle={{ fontSize: '16px' }}
              />
            </Col>
          </Row>
        </Card>

        {/* 按大洲分组显示国家 */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={5}>按大洲分组的国家列表</Title>
          {Object.keys(supportedCountries.continentGroups || {}).length > 0 ? (
            Object.entries(supportedCountries.continentGroups).map(([continent, countries]) => (
              <Card 
                key={continent} 
                size="small" 
                title={`${continent} (${countries.length}个国家)`}
                style={{ marginBottom: '12px' }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {countries.map((country, index) => (
                    <Tag key={index} color="blue">
                      {country.country}
                    </Tag>
                  ))}
                </div>
              </Card>
            ))
          ) : (
            <Empty description="暂无国家数据" size="small" />
          )}
        </div>

        {/* 添加新的国家模式 */}
        <Card title="添加新的国家模式" size="small">
          <Form
            form={countryForm}
            onFinish={handleAddCountryPattern}
            layout="vertical"
          >
            <Form.Item
              name="pattern"
              label="地理位置模式"
              rules={[{ required: true, message: '请输入地理位置模式' }]}
            >
              <Input placeholder="例如：中国.*北京|北京.*中国" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="country"
                  label="国家"
                  rules={[{ required: true, message: '请输入国家名称' }]}
                >
                  <Input placeholder="例如：中国" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="continent"
                  label="大洲"
                  rules={[{ required: true, message: '请选择大洲' }]}
                >
                  <Select placeholder="请选择大洲">
                    <Option value="亚洲">亚洲</Option>
                    <Option value="欧洲">欧洲</Option>
                    <Option value="北美洲">北美洲</Option>
                    <Option value="南美洲">南美洲</Option>
                    <Option value="非洲">非洲</Option>
                    <Option value="大洋洲">大洋洲</Option>
                    <Option value="南极洲">南极洲</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  添加模式
                </Button>
                <Button onClick={() => countryForm.resetFields()}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 地理位置统计 */}
        {locationStats && (
          <Card title={`地理位置统计 (${locationStats.analysisWindow || '30天'})`} size="small" style={{ marginTop: '16px' }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总会话数"
                  value={locationStats.totalSessions || 0}
                  prefix={<UserOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="不同位置数"
                  value={locationStats.uniqueLocations || 0}
                  prefix={<EnvironmentOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="不同国家数"
                  value={locationStats.uniqueCountries || 0}
                  prefix={<EnvironmentOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="不同大洲数"
                  value={locationStats.uniqueContinents || 0}
                  prefix={<EnvironmentOutlined />}
                />
              </Col>
            </Row>
            
            {/* 热门位置 */}
            <div style={{ marginTop: '16px' }}>
              <Title level={5}>热门登录位置 (Top 10)</Title>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {locationStats.topLocations?.slice(0, 10).map((item, index) => (
                  <Tag key={index} color="blue">
                    {item.location}: {item.count}次
                  </Tag>
                ))}
              </div>
            </div>
            
            {/* 热门国家 */}
            <div style={{ marginTop: '16px' }}>
              <Title level={5}>热门登录国家</Title>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {locationStats.topCountries?.map((item, index) => (
                  <Tag key={index} color="green">
                    {item.country}: {item.count}次
                  </Tag>
                ))}
              </div>
            </div>
            
            {/* 大洲分布 */}
            <div style={{ marginTop: '16px' }}>
              <Title level={5}>大洲分布</Title>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {locationStats.topContinents?.map((item, index) => (
                  <Tag key={index} color="purple">
                    {item.continent}: {item.count}次
                  </Tag>
                ))}
              </div>
            </div>
            
            {/* 不支持的位置 */}
            {locationStats.unsupportedLocations && locationStats.unsupportedLocations.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Title level={5}>不支持的位置</Title>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {locationStats.unsupportedLocations.map((item, index) => (
                    <Tag key={index} color="red">
                      {item.location}: {item.count}次
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}
      </Modal>

      {/* 用户风险测试弹窗 */}
      <Modal
        title="用户风险评估测试"
        open={userTestModalVisible}
        onCancel={() => {
          setUserTestModalVisible(false);
          userTestForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="测试说明"
          description="输入用户ID和测试参数，系统将模拟该用户的登录风险评估过程"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <Form
          form={userTestForm}
          onFinish={handleUserRiskTest}
          layout="vertical"
        >
          <Form.Item
            name="userId"
            label="用户ID"
            rules={[{ required: true, message: '请输入用户ID' }]}
          >
            <Input placeholder="请输入要测试的用户ID" />
          </Form.Item>
          
          <Form.Item
            name="ip"
            label="测试IP地址"
          >
            <Input placeholder="可选，默认为 127.0.0.1" />
          </Form.Item>
          
          <Form.Item
            name="location"
            label="测试地理位置"
          >
            <Input placeholder="可选，默认为 本地网络" />
          </Form.Item>
          
          <Form.Item
            name="deviceInfo"
            label="设备信息 (JSON格式)"
          >
            <Input.TextArea 
              rows={4}
              placeholder='可选，JSON格式，例如: {"userAgent": "Mozilla/5.0...", "platform": "Win32"}'
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                开始测试
              </Button>
              <Button onClick={() => userTestForm.resetFields()}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 配置历史模态框 */}
      <Modal
        title="配置修改历史"
        open={configHistoryVisible}
        onCancel={() => setConfigHistoryVisible(false)}
        footer={[
          <Button key="close" onClick={() => setConfigHistoryVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <Table
          dataSource={configHistory}
          columns={[
            {
              title: '配置项',
              dataIndex: 'configKey',
              key: 'configKey',
              width: 200,
              render: (key) => {
                const keyMap = {
                  'risk_assessment.threshold': '风险阈值',
                  'risk_assessment.weights': '权重配置',
                  'risk_assessment.location': '地理位置配置',
                  'risk_assessment.device': '设备配置',
                  'risk_assessment.time_pattern': '时间模式配置',
                  'risk_assessment.frequency': '频率配置',
                  'risk_assessment.ip_reputation': 'IP信誉配置',
                  'risk_assessment.analysis_days': '分析天数',
                  'risk_assessment.special_rules': '特殊规则'
                };
                return (
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      {keyMap[key] || key}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {key}
                    </div>
                  </div>
                );
              }
            },
            {
              title: '配置值',
              dataIndex: 'configValue',
              key: 'configValue',
              width: 300,
              render: (value) => {
                if (typeof value === 'object' && value !== null) {
                  return (
                    <div style={{ maxHeight: '100px', overflow: 'auto' }}>
                      <pre style={{ 
                        fontSize: '11px', 
                        margin: 0, 
                        padding: '4px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    </div>
                  );
                } else {
                  return <Tag color="blue">{String(value)}</Tag>;
                }
              }
            },
            {
              title: '描述',
              dataIndex: 'description',
              key: 'description',
              width: 120,
              ellipsis: true,
            },
            {
              title: '版本',
              dataIndex: 'version',
              key: 'version',
              width: 80,
            },
            {
              title: '修改人',
              key: 'updatedBy',
              width: 100,
              render: (_, record) => {
                const username = record.updatedBy?.username;
                const userId = record.updatedBy?.id;
                if (username) {
                  return username;
                } else if (userId) {
                  return (
                    <div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        ID: {userId.substring(0, 8)}...
                      </div>
                    </div>
                  );
                } else {
                  return <span style={{ color: '#999' }}>未知</span>;
                }
              }
            },
            {
              title: '修改时间',
              dataIndex: 'updatedAt',
              key: 'updatedAt',
              width: 160,
              render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss')
            }
          ]}
          rowKey="id"
          pagination={{
            pageSize: 8,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
          }}
          size="small"
          scroll={{ y: 400 }}
        />
      </Modal>

      {/* 缓存统计模态框 */}
      <Modal
        title="配置缓存统计"
        open={cacheStatsVisible}
        onCancel={() => setCacheStatsVisible(false)}
        footer={[
          <Button key="refresh" onClick={handleViewCacheStats}>
            刷新
          </Button>,
          <Button key="close" onClick={() => setCacheStatsVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {cacheStats && (
          <div>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={8}>
                <Statistic
                  title="缓存大小"
                  value={cacheStats.cacheSize || 0}
                  prefix={<SettingOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="缓存键数量"
                  value={cacheStats.cacheKeys?.length || 0}
                  prefix={<SettingOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="TTL (秒)"
                  value={Math.round((cacheStats.cacheTTL || 0) / 1000)}
                  suffix="s"
                  prefix={<SettingOutlined />}
                />
              </Col>
            </Row>
            
            <div style={{ marginTop: '16px' }}>
              <Title level={5}>缓存键列表</Title>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {cacheStats.cacheKeys?.map((key, index) => (
                  <Tag key={index} color="blue">
                    {key}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RiskManagement;
